/**
 * Scroll restoration for the list -> certificate -> list round trip.
 *
 * Two different behaviours, and they are NOT the same rule applied twice:
 *
 * - **Forward** (list -> a certificate) always lands at the TOP. You tapped the
 *   ninth card; opening its page already scrolled halfway down is meaningless,
 *   because the two pages share no vertical structure.
 * - **Back** (certificate -> list) returns to where that card WAS. Landing at
 *   the top of a long list after opening one item is the classic way to lose
 *   someone's place, and it is worse here than on the web generally because the
 *   back gesture is how you browse a collection one item at a time.
 *
 * The app scrolls the DOCUMENT (verified — there is no inner scroll container),
 * so `window.scrollY` is the whole story.
 *
 * A module-level Map rather than sessionStorage, deliberately: the position
 * should survive a route change, not a page reload. Coming back to the app
 * fresh and being dropped mid-list — with no memory of why — is disorienting in
 * a way that returning from a certificate you just opened is not.
 */
const positions = new Map<string, number>()

/** Remember where `key` is scrolled to right now. Call BEFORE navigating away. */
export const saveScrollPosition = (key: string): void => {
  if (typeof window === 'undefined') return
  positions.set(key, window.scrollY)
}

/** The remembered position for `key`, or undefined. Does not clear it. */
export const getScrollPosition = (key: string): number | undefined => positions.get(key)

export const clearScrollPosition = (key: string): void => {
  positions.delete(key)
}

/**
 * Jump to the top, without smooth behaviour.
 *
 * `behavior: 'auto'` matters: a smooth scroll would animate at the same moment
 * the page is sliding in from the right, and the two motions read as one janky
 * compound movement rather than as a page arriving.
 */
export const scrollToTop = (): void => {
  if (typeof window === 'undefined') return
  window.scrollTo({ top: 0, behavior: 'auto' })
}

/**
 * How many pages of an infinitely-scrolled list were loaded when it was left.
 *
 * Scroll position alone cannot restore an infinite list, and that is the whole
 * reason this lives beside it rather than in the page: coming back with one
 * page loaded and a `scrollTo(4000)` scrolls a document that is only 900px
 * tall, so the browser clamps to the bottom and the restore silently does
 * nothing. The page count has to come back FIRST, and the scroll only once the
 * rows it refers to exist.
 *
 * Same module-level Map, for the same reason: survive a route change, not a
 * reload. Arriving fresh should start at page one.
 */
const loadedPages = new Map<string, number>()

export const saveLoadedPages = (key: string, pages: number): void => {
  loadedPages.set(key, pages)
}

export const getLoadedPages = (key: string): number | undefined => loadedPages.get(key)

export const clearLoadedPages = (key: string): void => {
  loadedPages.delete(key)
}
